import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EmodeCasePurchaseCopy = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const redirectToLoginPage = () => {
    if (!userToken) return <Redirect exact to="/sparepartsLogin" />;
    return null;
  };

  const [invno, setInvno] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ edit mode + save state
  const [mode, setMode] = useState("view"); // view | edit
  const [saving, setSaving] = useState(false);

  // fetched (full) rows
  const [rows, setRows] = useState([]);
  const [baseRows, setBaseRows] = useState([]);

  // local filters
  const [fltItem, setFltItem] = useState("");
  const [fltCase, setFltCase] = useState("");

  // ✅ pending cases (from backend)
  const [pendingCaseFlag, setPendingCaseFlag] = useState(false);
  const [pendingCases, setPendingCases] = useState([]); // [{casenumber: "..."}]
  const [pendingOpen, setPendingOpen] = useState(false);

  const deepClone = (v) => JSON.parse(JSON.stringify(v));

  const normOpt = (v) => {
    let s = String(v ?? "").trim();
    if (!s) return "";
    const l = s.toLowerCase();
    if (l === "undefined" || l === "null") return "";
    return s;
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text ?? ""));
      toast.success("Copied", { toastId: `copied_${String(text ?? "").slice(0, 20)}` });
    } catch (e) {
      toast.error("Copy failed", { toastId: "copy_fail" });
    }
  };

  const fetchRows = useCallback(async () => {
    const inv = normOpt(invno);

    if (!inv) {
      toast.error("Inv No is required", { toastId: "need_inv" });
      return;
    }

    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("invno", inv);

      const resp = await fetch(`/case-scan/save-copy/fetch?${qs.toString()}`, {
        method: "GET",
        headers: { "Content-Type": "application/json", sptoken: userToken },
      });

      if (!resp.ok) {
        const t = await resp.text();
        toast.error(t || "Fetch failed", { toastId: "fetch_http" });
        setRows([]);
        setBaseRows([]);
        setPendingCaseFlag(false);
        setPendingCases([]);
        return;
      }

      const data = await resp.json();
      console.log(data)
      if (data?.type !== "success") {
        toast.error(data?.message || "Fetch failed", { toastId: "fetch_fail" });
        setRows([]);
        setBaseRows([]);
        setPendingCaseFlag(false);
        setPendingCases([]);
        return;
      }

      // ✅ pending case bind
      const pflag = data?.pendingCaseflag;
      const pcases = Array.isArray(data?.pendingCase) ? data.pendingCase : [];
      setPendingCaseFlag(pflag);
      //console.log(pendingCaseFlag)
      setPendingCases(pcases);

      // ✅ NOTE: expects detail rows now (must contain dpono/subledgerid/isstock/allocqty)
      const normalized = (data.rows || []).map((r, idx) => ({
        __id: `${String(r.casenumber ?? "").trim()}||${String(r.invno ?? "").trim()}||${String(r.dpono ?? "").trim()}||${String(r.subledgerid ?? "").trim()}||${String(r.itemid ?? "").trim()}||${r.isstock ? 1 : 0}||${idx}`,
        ...r,
        partno: String(r.partno ?? "").trim(),
        itemcode: String(r.itemcode ?? r.partno ?? "").trim(),
        casenumber: String(r.casenumber ?? "").trim(),
        rackname: String(r.rackname ?? "").trim(),
        partdescription: String(r.partdescription ?? "").trim(),
        transitqty: Number(r.transitqty ?? 0),
        allocqty: Number(r.allocqty ?? 0),
        isstock: !!r.isstock,
      }));

      setRows(normalized);
      setBaseRows(deepClone(normalized));
      setMode("view");

      setFltItem("");
      setFltCase("");

      toast.success(`Loaded ${data.count ?? normalized.length} rows`, {
        toastId: "fetch_ok",
      });
    } catch (e) {
      toast.error(e?.message || "Fetch failed", { toastId: "fetch_ex" });
      setRows([]);
      setBaseRows([]);
      setPendingCaseFlag(false);
      setPendingCases([]);
    } finally {
      setLoading(false);
    }
  }, [invno, userToken]);

  // ✅ filters
  const filteredRows = useMemo(() => {
    const a = normOpt(fltItem).toLowerCase();
    const b = normOpt(fltCase).toLowerCase();
    if (!a && !b) return rows;

    return rows.filter((r) => {
      const p = String(r.itemcode ?? r.partno ?? "").toLowerCase();
      const c = String(r.casenumber ?? "").toLowerCase();
      return (!a || p.includes(a)) && (!b || c.includes(b));
    });
  }, [rows, fltItem, fltCase]);

  // ✅ changed tracking
  const baseMap = useMemo(() => {
    const m = new Map();
    for (const r of baseRows) m.set(r.__id, r);
    return m;
  }, [baseRows]);

  const changedMap = useMemo(() => {
    const out = new Map();
    for (const r of rows) {
      const b = baseMap.get(r.__id);
      if (!b) continue;
      if (Number(r.allocqty ?? 0) !== Number(b.allocqty ?? 0)) out.set(r.__id, true);
    }
    return out;
  }, [rows, baseMap]);

  const changedCount = changedMap.size;

  const onModeChange = useCallback(
    (e, v) => {
      if (!v) return;
      if (mode === "edit" && v === "view" && changedCount > 0) {
        setRows(deepClone(baseRows));
        toast.info("Changes discarded", { toastId: "discard" });
      }
      setMode(v);
    },
    [mode, changedCount, baseRows]
  );

  const setAllocForRow = useCallback((id, value, max) => {
    let n = Number(value);
    if (!Number.isFinite(n)) n = 0;
    if (n < 0) n = 0;

    const m = Number(max ?? 0);
    // optional clamp to transitqty
    if (Number.isFinite(m) && m > 0 && n > m) n = m;

    setRows((prev) => prev.map((r) => (r.__id === id ? { ...r, allocqty: n } : r)));
  }, []);

  const saveChanges = useCallback(async () => {
    if (changedCount === 0) {
      toast.info("No changes to save", { toastId: "no_changes" });
      return;
    }

    setSaving(true);
    try {
      const payloadRows = rows
        .filter((r) => changedMap.has(r.__id))
        .map((r) => ({
          casenumber: normOpt(r.casenumber),
          invno: normOpt(r.invno),
          dpono: normOpt(r.dpono),
          subledgerid: r.subledgerid ?? null,
          itemid: r.itemid ?? 0,
          isStock: !!r.isstock,
          allocqty: Number(r.allocqty ?? 0),
        }));

      const resp = await fetch(`/case-scan/save-copy/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", sptoken: userToken },
        body: JSON.stringify({
          invno: normOpt(invno),
          rows: payloadRows,
        }),
      });

      if (!resp.ok) {
        const t = await resp.text();
        toast.error(t || "Update failed", { toastId: "upd_http" });
        return;
      }

      const data = await resp.json();
      if (data?.type !== "success") {
        toast.error(data?.message || "Update failed", { toastId: "upd_fail" });
        return;
      }

      toast.success(data?.message || "Updated", { toastId: "upd_ok" });
      setBaseRows(deepClone(rows));
      setMode("view");
    } catch (e) {
      toast.error(e?.message || "Update failed", { toastId: "upd_ex" });
    } finally {
      setSaving(false);
    }
  }, [changedCount, rows, changedMap, userToken, invno]);

  const columns = useMemo(
    () => [
      { field: "itemcode", headerName: "Item Code", width: 160 },
      { field: "partdescription", headerName: "Description", width: 320 },
      { field: "casenumber", headerName: "Case No", width: 500 },
      { field: "dpono", headerName: "DPO", width: 150 },
      { field: "isstock", headerName: "Stock", width: 90, renderCell: (p) => (p.value ? "Yes" : "No") },
      { field: "rackname", headerName: "Rack", width: 140 },
      { field: "transitqty", headerName: "Transit", width: 110, type: "number" },
      {
        field: "allocqty",
        headerName: "Alloc Qty",
        width: 120,
        type: "number",
        editable: mode === "edit",
        valueGetter: (p) => Number(p.row.allocqty ?? 0),
        valueSetter: (p) => {
          setAllocForRow(p.row.__id, p.value, p.row.transitqty);
          let n = Number(p.value);
          if (!Number.isFinite(n)) n = 0;
          if (n < 0) n = 0;
          const max = Number(p.row.transitqty ?? 0);
          if (max > 0 && n > max) n = max;
          return { ...p.row, allocqty: n };
        },
      },
      { field: "balanceqty", headerName: "Balance", width: 110, type: "number" },
    ],
    [mode, setAllocForRow]
  );

  const showPendingBtn = pendingCaseFlag;

  return (
    <div>
      <Sidebar
        Title={"Inv Summary (Transit vs Alloc) - Copy"}
        expandedPanel={"panel2"}
        children={
          <Box p={2}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      Invoice Summary — Report
                    </Typography>

                    <ToggleButtonGroup size="small" value={mode} exclusive onChange={onModeChange}>
                      <ToggleButton value="view">VIEW</ToggleButton>
                      <ToggleButton value="edit" disabled={!rows.length}>
                        EDIT
                      </ToggleButton>
                    </ToggleButtonGroup>

                    <Chip
                      label={`Rows: ${filteredRows.length}${rows.length !== filteredRows.length ? ` / ${rows.length}` : ""}`}
                      color="info"
                      variant="filled"
                    />
                    <Chip
                      label={`Changed: ${changedCount}`}
                      color={changedCount ? "warning" : "default"}
                      variant={changedCount ? "filled" : "outlined"}
                    />
                  </Stack>

                  <Divider />

                  {/* Fetch row */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                    <TextField
                      label="Inv No"
                      value={invno}
                      onChange={(e) => setInvno(e.target.value)}
                      size="small"
                      sx={{ minWidth: 240 }}
                    />

                    <Button variant="contained" startIcon={<SearchIcon />} onClick={fetchRows} disabled={loading}>
                      Fetch
                    </Button>

                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows} disabled={loading || !rows.length}>
                      Refresh
                    </Button>

                    {/* ✅ Pending cases button */}
                    {showPendingBtn && (
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<PendingActionsIcon />}
                        onClick={() => setPendingOpen(true)}
                      >
                        Pending Cases ({pendingCases.length})
                      </Button>
                    )}

                    <Box sx={{ flex: 1 }} />

                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<SaveIcon />}
                      onClick={saveChanges}
                      disabled={mode !== "edit" || saving || changedCount === 0}
                    >
                      {saving ? "Saving..." : "Save"}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<RestoreIcon />}
                      onClick={() => {
                        setRows(deepClone(baseRows));
                        toast.info("Reverted", { toastId: "revert" });
                      }}
                      disabled={mode !== "edit" || saving || changedCount === 0}
                    >
                      Revert
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={() => {
                        setFltItem("");
                        setFltCase("");
                      }}
                      disabled={!fltItem && !fltCase}
                    >
                      Clear Filters
                    </Button>
                  </Stack>

                  {/* Filters row */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                    <TextField
                      label="Filter Item Code (partno)"
                      value={fltItem}
                      onChange={(e) => setFltItem(e.target.value)}
                      size="small"
                      sx={{ minWidth: 280 }}
                    />

                    <TextField
                      label="Filter Case No"
                      value={fltCase}
                      onChange={(e) => setFltCase(e.target.value)}
                      size="small"
                      sx={{ minWidth: 220 }}
                    />

                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      Tip: type partial text (contains match)
                    </Typography>
                  </Stack>

                  <Divider />

                  <Box sx={{ height: 560, width: "100%" }}>
                    {loading ? (
                      <Stack sx={{ height: "100%" }} alignItems="center" justifyContent="center" spacing={2}>
                        <CircularProgress />
                        <Typography>Loading...</Typography>
                      </Stack>
                    ) : (
                      <DataGrid
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(r) => r.__id}
                        disableRowSelectionOnClick
                        density="compact"
                        slots={{ toolbar: GridToolbar }}
                        getRowClassName={(p) => (changedMap.has(p.row.__id) ? "row-changed" : "")}
                        sx={{ "& .row-changed": { backgroundColor: "rgba(255, 193, 7, 0.18)" } }}
                      />
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* ✅ Pending Cases Dialog */}
            <Dialog open={pendingOpen} onClose={() => setPendingOpen(false)} fullWidth maxWidth="sm">
              <DialogTitle>Pending Cases ({pendingCases.length})</DialogTitle>

              <DialogContent dividers>
                {!pendingCases.length ? (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    No pending cases.
                  </Typography>
                ) : (
                  <List dense>
                    {pendingCases.map((x, i) => {
                      const cno = normOpt(x?.casenumber);
                      return (
                        <ListItem
                          key={`${cno}__${i}`}
                          secondaryAction={
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Copy">
                                <IconButton size="small" onClick={() => copyText(cno)}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {/* <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setFltCase(cno);
                                  setPendingOpen(false);
                                  toast.info(`Filtered by case: ${cno}`, { toastId: "case_filter" });
                                }}
                              >
                                Use
                              </Button> */}
                            </Stack>
                          }
                        >
                          <ListItemText primary={cno || "(blank)"} />
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={() => {
                    const all = pendingCases.map((x) => normOpt(x?.casenumber)).filter(Boolean).join("\n");
                    if (!all) return toast.info("Nothing to copy", { toastId: "copy_none" });
                    copyText(all);
                  }}
                  disabled={!pendingCases.length}
                >
                  Copy All
                </Button>
                <Button variant="contained" onClick={() => setPendingOpen(false)}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        }
      />
      {redirectToLoginPage()}
    </div>
  );
};

export default EmodeCasePurchaseCopy;